import { CircularProgress } from '@civilgrid/ui/components';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { useSnapshot } from 'valtio';
import { getMergeHistory, type MergeHistoryEntry } from '~/api/civilsearch';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import Table from '~/components2/Table';
import store from '../../store';
import { columns } from './columns';

export default function History() {
  const { agency } = useSnapshot(store) as typeof store;
  const agencyId = agency?.id;

  const [history, setHistory] = useState<MergeHistoryEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (!agencyId) return;

    capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencyHistoryTabOpened, { agency_id: agencyId });

    let cancelled = false;
    setIsLoading(true);
    setIsError(false);

    getMergeHistory(agencyId)
      .then((res) => {
        if (cancelled) return;
        setHistory(res.history);
      })
      .catch(() => {
        if (cancelled) return;
        setIsError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [agencyId]);

  const table = useReactTable({
    data: history,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: true,
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    initialState: {
      sorting: [{ id: 'mergedAt', desc: true }],
    },
    getRowId: (row) => row.id,
  });

  if (isLoading) {
    return (
      <div data-testid="agency-history-loading">
        <CircularProgress indeterminate />
      </div>
    );
  }

  if (isError) {
    return <div data-testid="agency-history-error">Error fetching merge history for this agency.</div>;
  }

  return (
    <div className="flex h-full w-full flex-col space-y-4 overflow-y-clip">
      <h2 className="text-primary text-xl font-semibold" data-testid="agency-history-title">
        Merge History
      </h2>
      <div className="grow overflow-y-auto" data-testid="agency-history-table">
        {isEmpty(history) ? (
          <div data-testid="agency-history-empty">No merge history for this agency.</div>
        ) : (
          <Table id="cs-agency-history-v2" table={table} />
        )}
      </div>
    </div>
  );
}
